import {CommandBase} from "./commandBase";
import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {ResolveAction} from "../utils/resolveDifference";
import {SectionKey} from "../utils/branchDiff";

// Applies a list of ResolveActions atomically — undo reverts the whole
// batch. Used when the user clicks the Resolve button on a single diff
// row; that one row can produce 1 (scalar) or N (set-diff with N add/remove
// entries) actions, but they all share an undo step.
export class ResolveFieldFromOtherCommand extends CommandBase {
    actions: ResolveAction[];

    constructor(actions: ResolveAction[]) {
        super();
        this.actions = actions;
    }

    execute(data: RemoteConstantsModel): boolean {
        let mutated = false;
        for (const a of this.actions) {
            if (applyAction(data, a)) mutated = true;
        }
        return mutated;
    }
}

function applyAction(data: RemoteConstantsModel, a: ResolveAction): boolean {
    const record = findRecord(data, a.sectionKey, a.identity);
    if (!record) return false;

    if (a.path.length === 0) return false;
    const segs = a.path.slice(0, -1);
    const leaf = a.path[a.path.length - 1];

    // Walk to the parent of the leaf, creating intermediate dict containers
    // on demand so e.g. setting `values.attackBonus` works when `values`
    // didn't exist on the Current record at all. Path segments are taken
    // verbatim — a segment like "building.barracks" is treated as a single
    // dict key, not split further.
    let parent: any = record;
    for (const seg of segs) {
        if (parent[seg] === undefined || parent[seg] === null) {
            parent[seg] = {};
        }
        parent = parent[seg];
    }

    switch (a.kind) {
        case 'scalar': {
            const old = parent[leaf];
            if (a.value === undefined) {
                if (!(leaf in parent)) return false;
                delete parent[leaf];
                return true;
            }
            if (JSON.stringify(old) === JSON.stringify(a.value)) return false;
            parent[leaf] = a.value;
            return true;
        }
        case 'set-add': {
            if (!Array.isArray(parent[leaf])) parent[leaf] = [];
            if (parent[leaf].includes(a.value)) return false;
            parent[leaf].push(a.value);
            return true;
        }
        case 'set-remove': {
            if (!Array.isArray(parent[leaf])) return false;
            const before = parent[leaf].length;
            parent[leaf] = parent[leaf].filter((v: unknown) => v !== a.value);
            return parent[leaf].length !== before;
        }
        case 'obj-add': {
            if (!Array.isArray(parent[leaf])) parent[leaf] = [];
            const arr = parent[leaf] as Record<string, unknown>[];
            const sig = objSignature(a.value as Record<string, unknown>);
            // Skip if an element with identical content already exists.
            if (arr.some(o => objSignature(o) === sig)) return false;
            const cloned = JSON.parse(JSON.stringify(a.value)) as Record<string, unknown>;
            // Keep uids unique within the array — counter-suffixed reward uids
            // authored on different branches can collide on the numeric tail.
            if (typeof cloned.uid === 'string') {
                cloned.uid = uniqueUid(cloned.uid, new Set(arr.map(o => String(o.uid))));
            }
            arr.push(cloned);
            return true;
        }
        case 'obj-remove': {
            if (!Array.isArray(parent[leaf])) return false;
            const arr = parent[leaf] as Record<string, unknown>[];
            const sig = objSignature(a.value as Record<string, unknown>);
            const before = arr.length;
            parent[leaf] = arr.filter(o => objSignature(o) !== sig);
            return (parent[leaf] as unknown[]).length !== before;
        }
    }
}

// Content signature of a uid-bearing object, ignoring the (branch-local) uid —
// matches the diff engine's element pairing for objset changes.
function objSignature(o: Record<string, unknown>): string {
    const {uid, ...rest} = o;
    return JSON.stringify(rest);
}

// Returns `desired` if free, else bumps its trailing `.<n>` (or appends `.1`)
// until unique within `existing`.
function uniqueUid(desired: string, existing: Set<string>): string {
    if (!existing.has(desired)) return desired;
    const m = desired.match(/^(.*)\.(\d+)$/);
    const prefix = m ? m[1] : desired;
    let n = m ? parseInt(m[2], 10) : 0;
    let candidate: string;
    do { n++; candidate = `${prefix}.${n}`; } while (existing.has(candidate));
    return candidate;
}

function findRecord(data: RemoteConstantsModel, sectionKey: SectionKey, identity: string): any | undefined {
    switch (sectionKey) {
        // Metadata: match by uid (the diff's identity field is current.uid).
        case 'unitAttributes':       return data.unitAttributes?.find(m => m.uid === identity);
        case 'buildingAttributes':   return data.buildingAttributes?.find(m => m.uid === identity);
        case 'techActionAttributes': return data.techActionAttributes?.find(m => m.uid === identity);
        case 'techPassiveAttributes':return data.techPassiveAttributes?.find(m => m.uid === identity);
        case 'rewards':              return data.rewards?.find(m => m.uid === identity);
        // Entities: matched by id from the start.
        case 'units':                return data.units?.find(u => u.id === identity);
        case 'buildings':            return data.buildings?.find(b => b.id === identity);
        case 'tiles':                return data.tiles?.find(t => t.id === identity);
        case 'resources':            return data.resources?.find(r => r.id === identity);
        case 'techTree':             return data.techTree?.find(n => n.id === identity);
    }
}
